/// src/admin/pages/Users/AdminUsers.js

import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive"; // <--- Per distinguere mobile/desktop
import api from "../../../services/api";

// Sotto-componenti
import CreateUserForm from "./CreateUserForm";
import EditUserModal from "./EditUserModal";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);

  // Stati per form e modali
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);

  // Rileva se la larghezza è <= 768px => mobile
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    fetchUsers();
    fetchRestaurants();
  }, []);

  // Carica la lista utenti
  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (error) {
      console.error("Errore caricamento utenti:", error.response || error);
    }
  };

  // Carica i ristoranti
  const fetchRestaurants = async () => {
    try {
      const res = await api.get("/restaurants");
      setRestaurants(res.data);
    } catch (error) {
      console.error("Errore caricamento ristoranti:", error.response || error);
    }
  };

  // Crea un nuovo utente
  const handleCreateUser = async (newUser) => {
    try {
      await api.post("/users", newUser);
      setShowCreateForm(false);
      fetchUsers();
    } catch (error) {
      console.error("Errore creazione utente:", error.response || error);
    }
  };

  // Click su "modifica"
  const handleEditClick = (user) => {
    setEditUser(user);
    setShowEditModal(true);
  };

  // Aggiorna un utente
  const handleUpdateUser = async (updatedUser) => {
    try {
      await api.patch(`/users/${updatedUser.id}`, updatedUser);
      setShowEditModal(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Errore aggiornamento utente:", error.response || error);
    }
  };

  // Render “card layout” (mobile)
  const renderMobileCards = () => {
    return (
      <div style={styles.cardsWrapper}>
        {users.map((u) => {
          const restaurantName =
            restaurants.find((r) => r.id === u.restaurant_id)?.name || "-";
          return (
            <div key={u.id} style={styles.card}>
              <div style={styles.cardRow}>
                <strong>Username:</strong> {u.username}
              </div>
              <div style={styles.cardRow}>
                <strong>Email:</strong> {u.email}
              </div>
              <div style={styles.cardRow}>
                <strong>Ruolo:</strong> {u.role}
              </div>
              <div style={styles.cardRow}>
                <strong>Ristorante:</strong> {u.restaurant_id ? restaurantName : "-"}
              </div>
              <div style={styles.cardRow}>
                <strong>Attivo:</strong> {u.is_active ? "Sì" : "No"}
              </div>
              <div style={styles.cardActions}>
                <button
                  style={{ ...styles.editButton, marginRight: 8 }}
                  onClick={() => handleEditClick(u)}
                >
                  ✎
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render tabella (desktop)
  const renderDesktopTable = () => {
    return (
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Username</th>
            <th style={styles.th}>Email</th>
            <th style={styles.th}>Ruolo</th>
            <th style={styles.th}>Ristorante</th>
            <th style={styles.th}>Attivo</th>
            <th style={styles.th}>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const restaurantName =
              restaurants.find((r) => r.id === u.restaurant_id)?.name || "-";
            return (
              <tr key={u.id} style={styles.tr}>
                <td style={styles.td}>{u.username}</td>
                <td style={styles.td}>{u.email}</td>
                <td style={styles.td}>{u.role}</td>
                <td style={styles.td}>
                  {u.restaurant_id ? restaurantName : "-"}
                </td>
                <td style={styles.td}>{u.is_active ? "Sì" : "No"}</td>
                <td style={styles.td}>
                  <button style={styles.editButton} onClick={() => handleEditClick(u)}>
                    ✎
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Gestione Utenti</h1>

      <div style={styles.actions}>
        <button style={styles.createButton} onClick={() => setShowCreateForm(true)}>
          Crea Nuovo Utente
        </button>
      </div>

      {/* Form di creazione */}
      {showCreateForm && (
        <CreateUserForm
          onCancel={() => setShowCreateForm(false)}
          onSubmit={handleCreateUser}
          restaurants={restaurants}
        />
      )}

      {/* Se mobile => card layout, se desktop => tabella */}
      {users.length === 0 ? (
        <p>Nessun utente trovato.</p>
      ) : isMobile ? (
        renderMobileCards()
      ) : (
        renderDesktopTable()
      )}

      {/* Modal modifica */}
      {showEditModal && editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => {
            setShowEditModal(false);
            setEditUser(null);
          }}
          onUpdate={handleUpdateUser}
          restaurants={restaurants}
        />
      )}
    </div>
  );
}

// Palette e stili
const palette = {
  primary: "#FF8C00",    // Arancione scuro
  secondary: "#FF6600",  // Arancione acceso
  background: "#FAFAFA", // Bianco sporco
  card: "#FFFFFF",       // Bianco puro
  textDark: "#333333",
  textLight: "#FFFFFF",
};

const styles = {
  container: {
    backgroundColor: palette.background,
    minHeight: "100vh",
    padding: 20,
  },
  title: {
    color: palette.textDark,
    fontSize: 24,
    marginBottom: 20,
  },
  actions: {
    marginBottom: 20,
  },
  createButton: {
    backgroundColor: palette.secondary,
    color: palette.textLight,
    border: "none",
    padding: "10px 15px",
    cursor: "pointer",
    borderRadius: 4,
  },

  // ------------------------------
  // Tabella (desktop)
  // ------------------------------
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: palette.card,
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
  },
  th: {
    backgroundColor: palette.secondary,
    color: palette.textLight,
    textAlign: "left",
    padding: 10,
    fontWeight: "bold",
  },
  tr: {
    borderBottom: "1px solid #ddd",
  },
  td: {
    padding: 10,
    color: palette.textDark,
    verticalAlign: "middle",
  },
  editButton: {
    backgroundColor: palette.primary,
    color: palette.textLight,
    border: "none",
    padding: "5px 10px",
    cursor: "pointer",
    borderRadius: 4,
  },

  // ------------------------------
  // Card layout (mobile)
  // ------------------------------
  cardsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  card: {
    backgroundColor: palette.card,
    border: "1px solid #EEE",
    borderRadius: 6,
    padding: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  },
  cardRow: {
    marginBottom: 6,
    color: palette.textDark,
  },
  cardActions: {
    marginTop: 10,
    display: "flex",
    gap: 8,
  },
};
